import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

export enum FundType {
  IsibayaDirect = 'IsibayaDirect',
  EarlyStateFund = 'EarlyStateFund',
  FundOfFunds = 'FundOfFunds'
}

export type ApplicationType = 'Isibaya' | 'Fund of Funds' | 'Early Stage';

export const FUND_TYPE_LABELS: Record<FundType, string> = {
  [FundType.IsibayaDirect]: 'Isibaya Direct',
  [FundType.EarlyStateFund]: 'Early Stage Fund',
  [FundType.FundOfFunds]: 'Fund of Funds'
};

export const FUND_TYPE_DESCRIPTIONS: Record<FundType, string> = {
  [FundType.IsibayaDirect]: 'Direct investments into businesses and projects delivering sustainable economic value.',
  [FundType.EarlyStateFund]: 'Supporting early-stage businesses with high growth potential and developmental impact.',
  [FundType.FundOfFunds]: 'Investing through fund managers who deploy capital to a diversified portfolio of qualifying businesses.'
};

export const FUND_TYPE_TO_APPLICATION_TYPE: Record<FundType, ApplicationType> = {
  [FundType.IsibayaDirect]: 'Isibaya',
  [FundType.EarlyStateFund]: 'Early Stage',
  [FundType.FundOfFunds]: 'Fund of Funds'
};

@Injectable({ providedIn: 'root' })
export class FundTypeService {
  private readonly STORAGE_KEY = 'pic_fund_type';
  private _selected: BehaviorSubject<FundType | null>;
  readonly selected$: Observable<FundType | null>;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    const initial = isPlatformBrowser(this.platformId) ? this.loadFromStorage() : null;
    this._selected = new BehaviorSubject<FundType | null>(initial);
    this.selected$ = this._selected.asObservable();
  }

  get selected(): FundType | null {
    return this._selected.value;
  }

  get selectedLabel(): string {
    return this._selected.value ? FUND_TYPE_LABELS[this._selected.value] : '';
  }

  get selectedDescription(): string {
    return this._selected.value ? FUND_TYPE_DESCRIPTIONS[this._selected.value] : '';
  }

  get selectedApplicationType(): ApplicationType | null {
    const selected = this._selected.value;
    return selected ? FUND_TYPE_TO_APPLICATION_TYPE[selected] : null;
  }

  select(type: FundType): void {
    this._selected.next(type);
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem(this.STORAGE_KEY, type);
    }
  }

  clear(): void {
    this._selected.next(null);
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem(this.STORAGE_KEY);
    }
  }

  private loadFromStorage(): FundType | null {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      return stored && (Object.values(FundType) as string[]).includes(stored)
        ? (stored as FundType)
        : null;
    } catch {
      return null;
    }
  }
}
